/* eslint-disable camelcase */
import { useCallback, useEffect } from 'react';
import { getAdminAccess } from '~/api/adminMfa';
import {
    setAdminEligibility,
    setAdminMfaCheckFailed,
    setAdminMfaChecking,
    setAdminMfaSession
} from '~/redux/modules/adminMfa';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { applyAdminMfaToken } from './useAdminSessionActions';

/**
 * Resolves, from the server, whether this member may enter Admin and whether
 * their admin session is still alive.
 *
 * Runs on every authenticated page load rather than only on entry to Admin.
 * Two reasons: the sidebar decides whether to render the Admin tab on every
 * page anyway, and the expiry warning has to reach a member who has wandered
 * out of Admin — which it cannot do if it was never told the deadline.
 *
 * The response may carry a fresh session token. The token is held in memory, so
 * a reload leaves nothing behind; re-minting it here is what stops a refresh
 * from re-prompting for a code. Re-minting never slides the session — a reload
 * is not activity.
 *
 * Mounted once, app-wide, from App.tsx. Anything that only wants the callback
 * — a retry button, say — must pass `isAutoCheck: false`, or it mounts a second
 * copy of the mount effect and the endpoint is called twice on every load.
 *
 * @param {Object} [options] isAutoCheck false to skip the check on mount
 * @returns {Object} status, isChecking and a manual re-check callback
 */
const useAdminAccess = ({ isAutoCheck = true }: { isAutoCheck?: boolean } = {}) => {
    const dispatch = useAppDispatch();

    const { id } = useAppSelector(({ profile }) => profile);
    const { isAuthed } = useAppSelector(({ authentication }) => authentication);
    const status = useAppSelector(({ adminMfa }) => adminMfa.status);

    const onCheckAccess = useCallback(async () => {
        if (!id || !isAuthed) return;

        dispatch(setAdminMfaChecking());

        try {
            const response = await getAdminAccess(id);

            dispatch(setAdminEligibility({
                canAccessAdmin: !!response?.can_access_admin,
                canAccessWelfareAdmin: !!response?.can_access_welfare_admin
            }));

            const mfa = response?.mfa || {};

            // Present only when a live session matched the binding tuple *and*
            // this tab needed a new token. Absent means keep the current one.
            applyAdminMfaToken(mfa);

            dispatch(setAdminMfaSession(mfa));
        } catch (error) {
            // A failed check must not be read as "session valid" — that would
            // render admin chrome over a session the server has expired.
            //
            // Reported as 'error' rather than 'required' because the two are
            // different answers: the server refused, versus the server never
            // replied. Only the first is something a code can fix, and the gate
            // offers a retry for the second.
            dispatch(setAdminMfaCheckFailed());
        }
    }, [dispatch, id, isAuthed]);

    useEffect(() => {
        if (!isAutoCheck) return;

        onCheckAccess();
    }, [isAutoCheck, onCheckAccess]);

    return {
        status,
        isChecking: status === 'checking' || status === 'unknown',
        onCheckAccess
    };
};

export default useAdminAccess;
